import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { DEFAULT_USERS_VISIBLE_COLUMNS } from "../components/UsersView/constants/columns";
import { DEFAULT_SESSIONS_VISIBLE_COLUMNS } from "../components/SessionsView/constants/columns";
import { DEFAULT_INVITES_VISIBLE_COLUMNS } from "../components/InvitesView/constants/columns";
import { DEFAULT_SUBSCRIPTIONS_VISIBLE_COLUMNS } from "../components/SubscriptionsView/constants/columns";
import { DEFAULT_VIDEOS_VISIBLE_COLUMNS } from "../components/VideosView/constants/columns";

// Users: matches API request body filters shape
const initialUserFilters = {
  userInput: "",
  subscriptionType: [],
  gender: null,
  dominantHand: null,
  userCreated: { from: null, to: null },
  secondarySubType: null,
  studentCredits: { min: null, max: null },
  reportCredits: { min: null, max: null },
};

const initialState = {
  currentTable: "users",
};

const initialUsersState = {
  filters: initialUserFilters,
  pagination: { page: 1, limit: 20 },
  visibleColumns: [...DEFAULT_USERS_VISIBLE_COLUMNS],
  filtersOpen: false,
  selectedUserId: null,
};

const initialSessionsState = {
  filters: {
    sessionName: "",
    sessionDate: { from: null, to: null },
  },
  pagination: { page: 1, limit: 20 },
  visibleColumns: [...DEFAULT_SESSIONS_VISIBLE_COLUMNS],
  filtersOpen: false,
  selectedSessionId: null,
};

const initialInvitesState = {
  filters: {
    senderName: null,
    invitee: null,
    inviteeName: null,
    status: null,
  },
  pagination: { page: 1, limit: 20 },
  visibleColumns: [...DEFAULT_INVITES_VISIBLE_COLUMNS],
  filtersOpen: false,
  selectedInviteId: null,
};

// Subscriptions: matches API request body filters shape
const initialSubscriptionsState = {
  filters: {
    userInput: "",
    autoRenewal: null,
    freeTrialUsed: null,
    startDate: { from: null, to: null },
    endDate: { from: null, to: null },
    lastUpdated: { from: null, to: null },
    subscriptionType: [],
  },
  pagination: { page: 1, limit: 20 },
  visibleColumns: [...DEFAULT_SUBSCRIPTIONS_VISIBLE_COLUMNS],
  filtersOpen: false,
  selectedSubscriptionId: null,
};

const initialVideosState = {
  filters: {
    userInput: "",
    videoSource: "",
    videoType: "",
    videoCreated: { from: null, to: null },
    golfClub: [],
    cameraAngle: "",
  },
  pagination: { page: 1, limit: 20 },
  sort: "videoCreated",
  order: "desc",
  visibleColumns: [...DEFAULT_VIDEOS_VISIBLE_COLUMNS],
  filtersOpen: false,
};

const AdminSearchContext = createContext(null);

export function AdminSearchProvider({ children, initial }) {
  const [usersState, setUsersState] = useState(
    initial?.usersState
      ? { ...initialUsersState, ...initial.usersState }
      : initialUsersState
  );
  const [sessionsState, setSessionsState] = useState(
    initial?.sessionsState
      ? { ...initialSessionsState, ...initial.sessionsState }
      : initialSessionsState
  );
  const [invitesState, setInvitesState] = useState(
    initial?.invitesState
      ? { ...initialInvitesState, ...initial.invitesState }
      : initialInvitesState
  );
  const [subscriptionsState, setSubscriptionsState] = useState(
    initial?.subscriptionsState
      ? { ...initialSubscriptionsState, ...initial.subscriptionsState }
      : initialSubscriptionsState
  );
  const [videosState, setVideosState] = useState(
    initial?.videosState
      ? { ...initialVideosState, ...initial.videosState }
      : initialVideosState
  );
  const [currentTable, setCurrentTableState] = useState(
    initial?.currentTable ?? initialState.currentTable
  );

  // === Reset helpers ===
  const resetUsersState = useCallback(() => {
    try {
      setUsersState(initialUsersState);
    } catch (e) {
      console.error("resetUsersState failed:", e);
    }
  }, []);

  const resetSessionsState = useCallback(() => {
    try {
      setSessionsState(initialSessionsState);
    } catch (e) {
      console.error("resetSessionsState failed:", e);
    }
  }, []);

  const resetInvitesState = useCallback(() => {
    try {
      setInvitesState(initialInvitesState);
    } catch (e) {
      console.error("resetInvitesState failed:", e);
    }
  }, []);

  const resetSubscriptionsState = useCallback(() => {
    try {
      setSubscriptionsState(initialSubscriptionsState);
    } catch (e) {
      console.error("resetSubscriptionsState failed:", e);
    }
  }, []);

  const resetVideosState = useCallback(() => {
    try {
      setVideosState(initialVideosState);
    } catch (e) {
      console.error("resetVideosState failed:", e);
    }
  }, []);

  // === Users state actions ===
  const setUsersFilters = useCallback(({ filters: newFilters }) => {
    try {
      setUsersState((prev) => ({
        ...prev,
        filters: newFilters,
        pagination: { ...prev.pagination, page: 1 },
      }));
    } catch (e) {
      console.error("setUsersFilters failed:", e);
    }
  }, []);

  const setUsersPagination = useCallback((patch) => {
    try {
      setUsersState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          ...(typeof patch === "function" ? patch(prev.pagination) : patch),
        },
      }));
    } catch (e) {
      console.error("setUsersPagination failed:", e);
    }
  }, []);

  const setUsersVisibleColumns = useCallback((updater) => {
    try {
      setUsersState((prev) => ({
        ...prev,
        visibleColumns:
          typeof updater === "function"
            ? updater(prev.visibleColumns)
            : updater,
      }));
    } catch (e) {
      console.error("setUsersVisibleColumns failed:", e);
    }
  }, []);

  const setUsersFiltersOpen = useCallback((value) => {
    try {
      setUsersState((prev) => ({ ...prev, filtersOpen: value }));
    } catch (e) {
      console.error("setUsersFiltersOpen failed:", e);
    }
  }, []);

  const setUsersSelectedUserId = useCallback((userId) => {
    try {
      setUsersState((prev) => ({ ...prev, selectedUserId: userId }));
    } catch (e) {
      console.error("setUsersSelectedUserId failed:", e);
    }
  }, []);

  // === Sessions actions ===
  const setSessionsFilters = useCallback((patch) => {
    try {
      setSessionsState((prev) => {
        const next = typeof patch === "function" ? patch(prev.filters) : patch;
        return { ...prev, filters: { ...prev.filters, ...next } };
      });
    } catch (e) {
      console.error("setSessionsFilters failed:", e);
    }
  }, []);

  const setSessionsPagination = useCallback((patch) => {
    try {
      setSessionsState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          ...(typeof patch === "function" ? patch(prev.pagination) : patch),
        },
      }));
    } catch (e) {
      console.error("setSessionsPagination failed:", e);
    }
  }, []);

  const setSessionsFiltersOpen = useCallback((value) => {
    try {
      setSessionsState((prev) => ({ ...prev, filtersOpen: value }));
    } catch (e) {
      console.error("setSessionsFiltersOpen failed:", e);
    }
  }, []);

  const setSessionsVisibleColumns = useCallback((updater) => {
    try {
      setSessionsState((prev) => ({
        ...prev,
        visibleColumns:
          typeof updater === "function"
            ? updater(prev.visibleColumns || initialSessionsVisibleColumns)
            : updater,
      }));
    } catch (e) {
      console.error("setSessionsVisibleColumns failed:", e);
    }
  }, []);

  const setSelectedSessionId = useCallback((sessionId) => {
    try {
      setSessionsState((prev) => ({
        ...prev,
        selectedSessionId: sessionId,
      }));
    } catch (e) {
      console.error("setSelectedSessionId failed:", e);
    }
  }, []);

  // === Invites actions ===
  const setInvitesFilters = useCallback((patch) => {
    try {
      setInvitesState((prev) => {
        const next = typeof patch === "function" ? patch(prev.filters) : patch;
        return { ...prev, filters: { ...prev.filters, ...next } };
      });
    } catch (e) {
      console.error("setInvitesFilters failed:", e);
    }
  }, []);

  const setInvitesPagination = useCallback((patch) => {
    try {
      setInvitesState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          ...(typeof patch === "function" ? patch(prev.pagination) : patch),
        },
      }));
    } catch (e) {
      console.error("setInvitesPagination failed:", e);
    }
  }, []);

  const setInvitesFiltersOpen = useCallback((value) => {
    try {
      setInvitesState((prev) => ({ ...prev, filtersOpen: value }));
    } catch (e) {
      console.error("setInvitesFiltersOpen failed:", e);
    }
  }, []);

  const setInvitesVisibleColumns = useCallback((updater) => {
    try {
      setInvitesState((prev) => ({
        ...prev,
        visibleColumns:
          typeof updater === "function"
            ? updater(prev.visibleColumns || initialInvitesVisibleColumns)
            : updater,
      }));
    } catch (e) {
      console.error("setInvitesVisibleColumns failed:", e);
    }
  }, []);

  const setSelectedInviteId = useCallback((inviteId) => {
    try {
      setInvitesState((prev) => ({
        ...prev,
        selectedInviteId: inviteId,
      }));
    } catch (e) {
      console.error("setSelectedInviteId failed:", e);
    }
  }, []);

  // === Subscriptions actions ===
  const setSubscriptionsFilters = useCallback((patch) => {
    try {
      setSubscriptionsState((prev) => {
        const next = typeof patch === "function" ? patch(prev.filters) : patch;
        return { ...prev, filters: { ...prev.filters, ...next } };
      });
    } catch (e) {
      console.error("setSubscriptionsFilters failed:", e);
    }
  }, []);

  const setSubscriptionsPagination = useCallback((patch) => {
    try {
      setSubscriptionsState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          ...(typeof patch === "function" ? patch(prev.pagination) : patch),
        },
      }));
    } catch (e) {
      console.error("setSubscriptionsPagination failed:", e);
    }
  }, []);

  const setSubscriptionsFiltersOpen = useCallback((value) => {
    try {
      setSubscriptionsState((prev) => ({ ...prev, filtersOpen: value }));
    } catch (e) {
      console.error("setSubscriptionsFiltersOpen failed:", e);
    }
  }, []);

  const setSubscriptionsVisibleColumns = useCallback((updater) => {
    try {
      setSubscriptionsState((prev) => ({
        ...prev,
        visibleColumns:
          typeof updater === "function"
            ? updater(prev.visibleColumns || initialSubscriptionsVisibleColumns)
            : updater,
      }));
    } catch (e) {
      console.error("setSubscriptionsVisibleColumns failed:", e);
    }
  }, []);

  const setSelectedSubscriptionId = useCallback((subscriptionId) => {
    try {
      setSubscriptionsState((prev) => ({
        ...prev,
        selectedSubscriptionId: subscriptionId,
      }));
    } catch (e) {
      console.error("setSelectedSubscriptionId failed:", e);
    }
  }, []);

  // === Videos actions ===
  const setVideosFilters = useCallback((patch) => {
    try {
      setVideosState((prev) => {
        const next = typeof patch === "function" ? patch(prev.filters) : patch;
        return { ...prev, filters: { ...prev.filters, ...next } };
      });
    } catch (e) {
      console.error("setVideosFilters failed:", e);
    }
  }, []);

  const setVideosPagination = useCallback((patch) => {
    try {
      setVideosState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          ...(typeof patch === "function" ? patch(prev.pagination) : patch),
        },
      }));
    } catch (e) {
      console.error("setVideosPagination failed:", e);
    }
  }, []);

  const setVideosFiltersOpen = useCallback((value) => {
    try {
      setVideosState((prev) => ({ ...prev, filtersOpen: value }));
    } catch (e) {
      console.error("setVideosFiltersOpen failed:", e);
    }
  }, []);

  const setVideosVisibleColumns = useCallback((updater) => {
    try {
      setVideosState((prev) => ({
        ...prev,
        visibleColumns:
          typeof updater === "function"
            ? updater(prev.visibleColumns)
            : updater,
      }));
    } catch (e) {
      console.error("setVideosVisibleColumns failed:", e);
    }
  }, []);

  const setVideosSort = useCallback((sort, order) => {
    try {
      setVideosState((prev) => ({
        ...prev,
        sort: sort ?? prev.sort,
        order: order ?? prev.order,
      }));
    } catch (e) {
      console.error("setVideosSort failed:", e);
    }
  }, []);

  const setCurrentTable = useCallback((table) => {
    try {
      setCurrentTableState(table);
    } catch (e) {
      console.error("setCurrentTable failed:", e);
    }
  }, []);

  const value = useMemo(
    () => ({
      // state
      usersState,
      sessionsState,
      invitesState,
      subscriptionsState,
      videosState,
      currentTable,
      usersFiltersOpen: usersState.filtersOpen,
      sessionsFiltersOpen: sessionsState.filtersOpen,
      invitesFiltersOpen: invitesState.filtersOpen,
      subscriptionsFiltersOpen: subscriptionsState.filtersOpen,
      videosFiltersOpen: videosState.filtersOpen,
      // actions
      setUsersPagination,
      setUsersVisibleColumns,
      setUsersFiltersOpen,
      setCurrentTable,
      setUsersFilters,
      setUsersSelectedUserId,
      resetUsersState,
      resetSessionsState,
      resetInvitesState,
      resetSubscriptionsState,
      setSessionsFilters,
      setSessionsPagination,
      setSessionsFiltersOpen,
      setSessionsVisibleColumns,
      setSelectedSessionId,
      setInvitesFilters,
      setInvitesPagination,
      setInvitesFiltersOpen,
      setInvitesVisibleColumns,
      setSelectedInviteId,
      setSubscriptionsFilters,
      setSubscriptionsPagination,
      setSubscriptionsFiltersOpen,
      setSubscriptionsVisibleColumns,
      setSelectedSubscriptionId,
      resetVideosState,
      setVideosFilters,
      setVideosPagination,
      setVideosSort,
      setVideosFiltersOpen,
      setVideosVisibleColumns,
    }),
    [
      currentTable,
      setCurrentTable,

      usersState,
      setUsersFilters,
      setUsersPagination,
      setUsersVisibleColumns,
      resetUsersState,
      setUsersSelectedUserId,
      setUsersFiltersOpen,

      sessionsState,
      setSessionsFilters,
      setSessionsPagination,
      setSessionsFiltersOpen,
      setSessionsVisibleColumns,
      setSelectedSessionId,
      resetSessionsState,

      invitesState,
      setInvitesFilters,
      setInvitesPagination,
      setInvitesFiltersOpen,
      setInvitesVisibleColumns,
      setSelectedInviteId,
      resetInvitesState,

      subscriptionsState,
      setSubscriptionsFilters,
      setSubscriptionsPagination,
      setSubscriptionsFiltersOpen,
      setSubscriptionsVisibleColumns,
      resetSubscriptionsState,
      setSelectedSubscriptionId,

      videosState,
      setVideosFilters,
      setVideosPagination,
      setVideosSort,
      setVideosFiltersOpen,
      setVideosVisibleColumns,
      resetVideosState,
    ]
  );

  return (
    <AdminSearchContext.Provider value={value}>
      {children}
    </AdminSearchContext.Provider>
  );
}

export function useAdminSearchStore() {
  const ctx = useContext(AdminSearchContext);
  if (!ctx)
    throw new Error(
      "useAdminSearchStore must be used within an AdminSearchProvider"
    );
  return ctx;
}
